import React, { useContext, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { get, groupBy } from 'es-toolkit/compat';
import { push } from 'src/core/engine/router';
import { BasketContext, BasketItemType } from 'src/components/Basket/BasketContext';
import { Price } from 'src/components/Helpers/Price';
import { BasketItemActions } from 'src/components/Basket/BasketItemActions';
import { UserKind } from 'src/components/BasketShared/ShareBasketViaEmailForm';
import { BasketItemFooter } from 'src/components/Basket/BasketItemFooter';
import { ItemCustomisationMap, groupById } from 'src/components/Basket/utils/basket-customisations';
import { CustomisationWithIdAndLocation, Targets } from 'src/components/CustomisationPage/feature/customisation.types';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import { artWorkEntryUrl } from 'src/components/CustomisationPage/utils/customisationsUrls';
import { EntryKind } from 'src/components/CustomisationPage/feature/customisation.actions';

import classes from './BasketItems.scss';
import { ItemCustomisations } from './ItemCustomisations';
import { BasketProduct } from './BasketProduct';

type BasketItemProps = {
    isPending: boolean;
    item: BasketItemType;
    userKind: UserKind;
    canCopyCustomisation: (customisationId: number, itemId: string) => boolean;
    getCopyParams: (itemId: string, customisation: CustomisationWithIdAndLocation) => Targets;
};

export const BasketItem: React.FC<BasketItemProps> = React.memo(
    ({ item, isPending, userKind, canCopyCustomisation, getCopyParams }) => {
        const { cartItemPricesInc } = useContext(BasketContext);
        const { customisations, cartItem } = item;
        const isBundle = cartItem.__typename === 'BundleCartItem';
        const bundleValues = get(cartItem, `bundle_options[0].values`, []);
        const bundleValuesByColor = groupBy(bundleValues, (bundleValue) => bundleValue.details.color.value);
        const isWearerBundleProduct = get(cartItem, 'product.__typename', '') === 'WearerBundlesProduct';

        const swatchCss = (url: string, hex: string) => {
            if (!url) {
                return { background: hex };
            }
            return { background: `url(${url}) center center`, backgroundSize: 'contain' };
        };
        const dispatch = useDispatch();

        let groupedCustomisations: ItemCustomisationMap = [];
        if (customisations.length !== 0) {
            groupedCustomisations = groupById(customisations);
        }

        /**
         * Return the items array directly rather than wrapping it in a new
         * object literal. react-redux 9 (paired with React 19) compares
         * selector results with `Object.is`, so the previous
         * `useSelector(s => ({ raw: s.cart.raw.items }))` produced a fresh
         * object on every render and triggered an infinite re-render loop
         * that ended with `Error: Too many re-renders` in `<FormInner>` and
         * the whole /basket page being unmounted by the error boundary.
         * `state.cart.raw.items` is a stable array reference from the
         * reducer, so `Object.is` correctly bails out between renders.
         */
        const rawItems = useSelector((state: any) => state.cart.raw?.items);

        const customiseProduct = useCallback(() => {
            const path = artWorkEntryUrl({ itemIds: [Number(cartItem.id)!], kind: EntryKind.New });
            dispatch(push(path));
        }, [cartItem.id, dispatch]);

        const matchingRawItem = rawItems?.find((rawItem) => rawItem?.id === cartItem.id);
        const discount = matchingRawItem?.prices?.discounts?.[0]?.amount?.value || 0;
        const discountItemPrice = cartItem.prices.price - discount / cartItem.quantity;
        const roundedDiscountItemPrice = Math.round((discountItemPrice + Number.EPSILON) * 100) / 100;
        const totalPrice = cartItem.prices.price * cartItem.quantity;

        return (
            <>
                <tr key={cartItem.id} className={classes.basketItem} data-loading={isPending}>
                    <td className={classes.basketItemProduct}>
                        <BasketProduct cartItem={cartItem} isPending={isPending} isBundle={isBundle} />
                    </td>
                    <td className={classes.basketItemActions}>
                        <BasketItemActions isPending={isPending} item={item} />
                    </td>
                    <td className={classes.basketItemPrice}>
                        <Price
                            price={cartItem.prices.price}
                            priceIncl={cartItemPricesInc.get(cartItem.id) ?? cartItem.prices.price}
                            className={classnames(classes.price, {
                                [classes.beforeDiscount]: discount,
                            })}
                        />
                        {discount > 0 && (
                            <Price
                                price={roundedDiscountItemPrice}
                                priceIncl={cartItemPricesInc.get(cartItem.id) ?? roundedDiscountItemPrice}
                                className={classes.price}
                            />
                        )}
                        <div className={classes.basketItemPriceInfo}>
                            <span className={classes.each}>each</span>
                            <small className={classes.withApplication}>(exc. VAT)</small>
                        </div>
                    </td>
                </tr>
                {customisations.length > 0 && (
                    <tr className={classes.basketItemCustomise}>
                        <td colSpan={4} className={classes.basketItemCustomiseContent}>
                            <ItemCustomisations
                                isPending={isPending}
                                groupedCustomisations={groupedCustomisations}
                                itemId={cartItem.id}
                                allowActions={!isBundle}
                                item={item}
                                canCopyCustomisation={canCopyCustomisation}
                                getCopyParams={getCopyParams}
                            />
                        </td>
                    </tr>
                )}
                {!isWearerBundleProduct && Object.keys(bundleValuesByColor).length > 0 && (
                    <tr className={classes.basketItemCustomise}>
                        <td colSpan={4} className={classes.basketItemCustomiseContent}>
                            <span className={classes.basketItemSwatchesAndSizesTitle}>Sizes ordered</span>
                            <div className={classes.basketItemSwatchesAndSizesTable}>
                                {Object.keys(bundleValuesByColor).map((color) => {
                                    const swatch_css = swatchCss(
                                        bundleValuesByColor[color][0].details.color.url,
                                        bundleValuesByColor[color][0].details.color.hex,
                                    );
                                    return (
                                        <div key={color} className={classes.basketItemSwatchesAndSizesTableRow}>
                                            <div className={classes.basketItemSwatchesAndSizesTableCell}>
                                                <span className={classes.basketItemSwatch} style={swatch_css} />
                                                <span className={classes.basketItemColour}>{color} - </span>
                                            </div>
                                            <div className={classes.basketItemSwatchesAndSizesTableCell}>
                                                {bundleValuesByColor[color].map(({ details, quantity }) => (
                                                    <span key={color + details.size} className={classes.basketItemSize}>
                                                        <strong>{details.size}</strong> x {quantity}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </td>
                    </tr>
                )}
                <tr className={classes.basketItemFooter}>
                    <td colSpan={4} className={classes.basketItemFooterPrimary}>
                        <BasketItemFooter
                            item={item}
                            userKind={userKind}
                            isPending={isPending}
                            itemPrice={totalPrice}
                            productDiscount={discount}
                            customiseProduct={customiseProduct}
                        />
                    </td>
                </tr>
            </>
        );
    },
);
